import { useEffect } from 'react';
import AccessDenied from '@/components/AccessDenied';
import { useAuth } from '@/hooks/useAuth';
import {
  COMPLIANCE_PERMISSIONS,
  hasPermission,
  hasAnyPermission,
} from '@/lib/compliancePermissions';
import {
  usePolicyFiles,
  useProjectComplianceProfiles,
  usePromotionMaterials,
  useSensitiveTerms,
  useComplianceRecheckTasks,
} from '@/hooks/useCompliance';
import {
  POLICY_FILE_STATUS_LABELS,
  PROJECT_COMPLIANCE_STATUS_LABELS,
  PROMOTION_MATERIAL_STATUS_LABELS,
  SENSITIVE_TERM_STATUS_LABELS,
  COMPLIANCE_RECHECK_STATUS_LABELS,
} from '@/lib/enumLabels';

function SummaryCard({
  title,
  value,
  hint,
}: {
  title: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="rounded-lg border bg-white p-5 shadow-sm">
      <div className="text-sm text-muted-foreground">{title}</div>
      <div className="mt-2 text-3xl font-semibold">{value}</div>
      <div className="mt-2 text-xs text-muted-foreground">{hint}</div>
    </div>
  );
}

export default function ComplianceHomePage() {
  const { permissions } = useAuth();
  const canView = hasAnyPermission(permissions, [
    COMPLIANCE_PERMISSIONS.POLICY_FILE_READ,
    COMPLIANCE_PERMISSIONS.PROJECT_STATUS_READ,
    COMPLIANCE_PERMISSIONS.PROMOTION_MATERIAL_READ,
    COMPLIANCE_PERMISSIONS.PROMOTION_REVIEW_READ,
    COMPLIANCE_PERMISSIONS.SENSITIVE_TERM_READ,
    COMPLIANCE_PERMISSIONS.RECHECK_TASK_READ,
  ]);

  const projectHook = useProjectComplianceProfiles();
  const policyHook = usePolicyFiles();
  const promotionHook = usePromotionMaterials();
  const termHook = useSensitiveTerms();
  const recheckHook = useComplianceRecheckTasks();

  useEffect(() => {
    if (!canView) {
      return;
    }
    if (hasPermission(permissions, COMPLIANCE_PERMISSIONS.PROJECT_STATUS_READ)) {
      void projectHook.fetchProfiles();
    }
    if (hasPermission(permissions, COMPLIANCE_PERMISSIONS.POLICY_FILE_READ)) {
      void policyHook.fetchPolicyFiles();
    }
    if (hasPermission(permissions, COMPLIANCE_PERMISSIONS.PROMOTION_MATERIAL_READ)) {
      void promotionHook.fetchMaterials();
    }
    if (hasPermission(permissions, COMPLIANCE_PERMISSIONS.SENSITIVE_TERM_READ)) {
      void termHook.fetchTerms();
    }
    if (hasPermission(permissions, COMPLIANCE_PERMISSIONS.RECHECK_TASK_READ)) {
      void recheckHook.fetchTasks();
    }
  }, [canView, permissions]);

  if (!canView) {
    return <AccessDenied resource="合规总览" />;
  }

  const activePolicies = policyHook.policyFiles.filter((item) => item.status === 'active').length;
  const blockedProjects = projectHook.profiles.filter(
    (item) => item.status === 'paused' || item.status === 'forbidden_sale',
  ).length;
  const pendingReviews = promotionHook.materials.filter(
    (item) => item.status === 'submitted' || item.status === 'under_review',
  ).length;
  const activeTerms = termHook.terms.filter((item) => item.status === 'active').length;
  const openRechecks = recheckHook.tasks.filter(
    (item) => item.status === 'pending' || item.status === 'in_progress',
  ).length;

  return (
    <div className="space-y-6">
      <div className="rounded-xl border bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 p-6 text-white shadow-sm">
        <div className="text-sm uppercase tracking-[0.2em] text-slate-300">Compliance Desk</div>
        <h1 className="mt-2 text-3xl font-semibold">合规风控总览</h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-200">
          聚合项目销售可售状态、政策文件时效、宣传材料审核命中和复核任务积压，作为方案、报价和订单前置闸门。
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <SummaryCard
          title="在效政策文件"
          value={String(activePolicies)}
          hint={`${POLICY_FILE_STATUS_LABELS.active} 文件数`}
        />
        <SummaryCard
          title="阻断项目"
          value={String(blockedProjects)}
          hint={`${PROJECT_COMPLIANCE_STATUS_LABELS.paused} / ${PROJECT_COMPLIANCE_STATUS_LABELS.forbidden_sale}`}
        />
        <SummaryCard
          title="待审宣传材料"
          value={String(pendingReviews)}
          hint={`${PROMOTION_MATERIAL_STATUS_LABELS.submitted} / ${PROMOTION_MATERIAL_STATUS_LABELS.under_review}`}
        />
        <SummaryCard
          title="启用敏感词"
          value={String(activeTerms)}
          hint={`${SENSITIVE_TERM_STATUS_LABELS.active} 词条数`}
        />
        <SummaryCard
          title="未闭环复核任务"
          value={String(openRechecks)}
          hint={`${COMPLIANCE_RECHECK_STATUS_LABELS.pending} / ${COMPLIANCE_RECHECK_STATUS_LABELS.in_progress}`}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="rounded-lg border bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold">当前重点风险</h2>
          <ul className="mt-4 space-y-3 text-sm">
            <li className="rounded-md bg-slate-50 px-3 py-3">
              若项目进入「{PROJECT_COMPLIANCE_STATUS_LABELS.paused}」或「{PROJECT_COMPLIANCE_STATUS_LABELS.forbidden_sale}」，
              新增方案/报价/订单必须被阻断，已签客户应进入复核队列。
            </li>
            <li className="rounded-md bg-slate-50 px-3 py-3">
              宣传材料只有在「{PROMOTION_MATERIAL_STATUS_LABELS.approved}」后才可发布，AI 命中词需要人工复核。
            </li>
            <li className="rounded-md bg-slate-50 px-3 py-3">
              政策文件离开「{POLICY_FILE_STATUS_LABELS.active}」后，应复核关联项目的可售状态和签约客户影响面。
            </li>
          </ul>
        </div>

        <div className="rounded-lg border bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold">状态分布</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs uppercase tracking-wide text-slate-500">项目可售状态</div>
              <div className="mt-2 space-y-1 text-sm">
                {projectHook.profiles.length === 0 ? (
                  <div className="text-muted-foreground">暂无数据</div>
                ) : (
                  <>
                    <div>
                      {PROJECT_COMPLIANCE_STATUS_LABELS.serviceable}：
                      {projectHook.profiles.filter((item) => item.status === 'serviceable').length}
                    </div>
                    <div>
                      {PROJECT_COMPLIANCE_STATUS_LABELS.special_approval_only}：
                      {projectHook.profiles.filter((item) => item.status === 'special_approval_only').length}
                    </div>
                    <div>
                      {PROJECT_COMPLIANCE_STATUS_LABELS.expert_review_only}：
                      {projectHook.profiles.filter((item) => item.status === 'expert_review_only').length}
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs uppercase tracking-wide text-slate-500">宣传审核</div>
              <div className="mt-2 space-y-1 text-sm">
                {promotionHook.materials.length === 0 ? (
                  <div className="text-muted-foreground">暂无数据</div>
                ) : (
                  <>
                    <div>
                      {PROMOTION_MATERIAL_STATUS_LABELS.approved}：
                      {promotionHook.materials.filter((item) => item.status === 'approved').length}
                    </div>
                    <div>
                      {PROMOTION_MATERIAL_STATUS_LABELS.changes_requested}：
                      {promotionHook.materials.filter((item) => item.status === 'changes_requested').length}
                    </div>
                    <div>
                      {PROMOTION_MATERIAL_STATUS_LABELS.rejected}：
                      {promotionHook.materials.filter((item) => item.status === 'rejected').length}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
